import _ from 'lodash';
import { hideMenu, showMenu } from 'react-contextmenu';
import { KEY_RECOIL } from '@/shared/config';
import { useReportStore } from '@/entities/report';
import { handleMarkReviewByCellOnly } from './reviewBeatsHelper';
import { dataChartHeatMap } from './rrInterval/heatMapChart/handler';

// Same value as leftColumn.tsx's module-level MENU_ID - not imported from there to avoid a
// leftColumn.tsx <-> useRrIntervalMenu.ts circular import.
const MENU_ID = 'BEATHR';

/**
 * Drives the R-R interval menu's bulk mark-reviewed context menu and heatmap review-progress data. Reads the heatmap status, RR overview origin and reviewed/bulk-reviewed collections from the report store, and exposes the context-menu id, click and menu-option handlers the render needs.
 *
 * @returns The heatmap status, RR overview origin, reviewed/bulk-reviewed collections, the menu id and the onItemMenuClick/handleClickOption handlers.
 */
const useRrIntervalMenu = () => {
  const dataHeatmapChartStatus = useReportStore((s) => s.dataHeatmapChartStatus);
  const rrOverviewOrigin = useReportStore((s) => s.rrOverviewOrigin);

  const bulkReviewedBeats = useReportStore((s) => s.bulkReviewedBeats);
  const setBulkReviewedBeats = useReportStore((s) => s.actions.setBulkReviewedBeats);
  const resetBulkReviewedBeats = useReportStore((s) => s.resetBulkReviewedBeats);
  const setResetBulkReviewedBeats = useReportStore((s) => s.actions.setResetBulkReviewedBeats);
  const reviewedBeatsChanges = useReportStore((s) => s.reviewedBeatsChanges);
  const setReviewedBeatsChanges = useReportStore((s) => s.actions.setReviewedBeatsChanges);
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Writes the beat options for the first report tab. Forwards the value to the report store action bound to that tab's key.
   *
   * @param v - The beat options value or updater to store.
   * @returns The result of the store's beat-options action.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(KEY_RECOIL.BEAT_HR, v);

  /**
   * Collects the populated R-R heatmap cells of a beat type so they can be bulk-reviewed. Filters the heatmap data down to cells with a value, projects id, index, value, originalReviewedCount, totalCount and templates, and returns a deep clone so callers can mutate it freely.
   *
   * @param beatType - Which beat type's heatmap column to read.
   * @returns A deep-cloned array of that beat type's populated heatmap cells.
   */
  const getCurrentListCells = (beatType: any) => {
    const listSelectedCells = (dataChartHeatMap as any)[beatType]
      .filter((x: any) => x.value)
      .map((x: any) => ({
        id: x.id,
        index: x.index,
        value: x.value,
        originalReviewedCount: x.originalReviewedCount,
        totalCount: x.value,
        templates: x.templates,
      }));
    return _.cloneDeep(listSelectedCells);
  };

  /**
   * Marks every beat in a beat type's heatmap cells as reviewed or unreviewed. Clones the bulk-review, reset and reviewed-change collections, lets handleMarkReviewByCellOnly mutate them against the populated cells from getCurrentListCells, then writes all three back to the store and mirrors them into the tab's beat options.
   *
   * @param isReviewed - Whether the beats should end up marked reviewed.
   * @param beatType - Which beat type's cells to update.
   * @returns Nothing.
   */
  const onUpdateMarkAll = ({ isReviewed, beatType }: { isReviewed: any; beatType: any }) => {
    const currentListCells = getCurrentListCells(beatType);
    const cloneBulkReview = _.cloneDeep(bulkReviewedBeats);
    const cloneResetList = _.cloneDeep(resetBulkReviewedBeats);
    const cloneReviewBeatChanges = _.cloneDeep(reviewedBeatsChanges);
    handleMarkReviewByCellOnly({
      currentListCells,
      cloneBulkReview,
      cloneResetList,
      cloneReviewBeatChanges,
      isReviewed,
      activeButton: beatType,
    });
    setBulkReviewedBeats(cloneBulkReview);
    setResetBulkReviewedBeats(cloneResetList);
    setReviewedBeatsChanges(cloneReviewBeatChanges);
    setBeatOptions((prev: any) => ({
      ...prev,
      bulkReviewedBeatsState: cloneBulkReview,
      resetBulkReviewedBeatsState: cloneResetList,
      reviewedBeatsChangesState: cloneReviewBeatChanges,
    }));
  };

  /**
   * Handles a mark-all selection made in the left-column context menu. Reads beatType and action off the menu data and forwards them to onUpdateMarkAll, treating markedAllReviewed as reviewed and anything else as unreviewed.
   *
   * @param e - The menu click event, unused here.
   * @param data - Menu item data carrying beatType and action.
   * @returns Nothing.
   */
  const onItemMenuClick = (e: any, data: any) => {
    const { beatType, action } = data;
    onUpdateMarkAll({
      isReviewed: action === 'markedAllReviewed',
      beatType,
    });
  };

  /**
   * Opens the bulk-action context menu at the pointer position for a left-column tab. Stops propagation, hides the shared menu first, then re-shows the requested menu on the next tick with the trigger data attached so its position updates.
   *
   * @param e - The click event supplying the pointer coordinates.
   * @param dataProps - Trigger data including the id of the menu to show.
   * @returns Nothing.
   */
  const handleClickOption = (e: any, dataProps: any) => {
    e.stopPropagation();
    hideMenu({
      id: MENU_ID,
    });
    setTimeout(() => {
      showMenu({
        position: { x: e.clientX, y: e.clientY },
        id: dataProps.id,
        data: dataProps,
      });
    }, 0);
  };

  return {
    dataHeatmapChartStatus,
    rrOverviewOrigin,
    bulkReviewedBeats,
    resetBulkReviewedBeats,
    reviewedBeatsChanges,
    menuId: MENU_ID,
    onItemMenuClick,
    handleClickOption,
  };
};

export default useRrIntervalMenu;
